import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, restaurantSession } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const emailValid = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);

export default function RestaurantLogin() {
  const nav = useNavigate();
  const [mode, setMode] = useState("login"); // login | register
  const [f, setF] = useState({
    email: "", password: "", confirmPassword: "",
    contact_name: "", phone: "", restaurant_name: "",
  });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);

  const setField = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const validate = () => {
    const errs = {};
    if (!emailValid(f.email)) errs.email = "Enter a valid email";
    if (!f.password || f.password.length < 6) errs.password = "Min 6 characters";
    if (mode === "register") {
      if (f.password !== f.confirmPassword) errs.confirmPassword = "Passwords don't match";
      if (!f.contact_name.trim()) errs.contact_name = "Required";
      if (!f.restaurant_name.trim()) errs.restaurant_name = "Required";
      if (!f.phone.trim()) errs.phone = "Required";
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const submit = async () => {
    if (!validate()) return;
    setBusy(true);
    try {
      const url = mode === "login" ? "/restaurant/login" : "/restaurant/register";
      const body = mode === "login"
        ? { email: f.email, password: f.password }
        : {
            email: f.email, password: f.password,
            contact_name: f.contact_name, phone: f.phone,
            restaurant_name: f.restaurant_name,
          };
      const res = await api.post(url, body);
      restaurantSession.set(res.data.token, res.data.restaurant);
      toast.success(mode === "login" ? "Welcome back!" : "Account created — welcome!");
      nav("/restaurant/dashboard");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
    setBusy(false);
  };

  return (
    <div className="min-h-screen bg-brand-dark text-brand-light font-body">
      <div className="max-w-md mx-auto px-6 py-14">
        <div className="flex items-center gap-2 mb-2">
          <span className="inline-block w-2.5 h-2.5 rounded-sm bg-brand-orange rotate-45" />
          <span className="font-heading font-bold text-2xl">
            <span className="text-brand-light">tab</span>
            <span className="text-brand-orange">dine</span>
          </span>
        </div>
        <div className="text-xs uppercase tracking-widest text-brand-light/50 mb-8">Restaurant Partner Portal</div>

        <h1 className="font-heading text-3xl font-bold">
          {mode === "login" ? "Restaurant Partner Login" : "Create your Tabdine account"}
        </h1>
        <p className="text-brand-light/60 mt-1 text-sm">
          {mode === "login"
            ? "The command centre for your kitchen, orders, and customers."
            : "Set up your restaurant in under a minute."}
        </p>

        <div className="mt-6 bg-white/5 border border-white/10 rounded-2xl p-5 space-y-3">
          {mode === "register" && (
            <>
              <div>
                <label className="text-xs text-brand-light/60">Your name</label>
                <Input
                  value={f.contact_name}
                  onChange={setField("contact_name")}
                  className="bg-white text-brand-dark"
                  data-testid="signup-name"
                />
                {errors.contact_name && <div className="text-xs text-red-400 mt-1">{errors.contact_name}</div>}
              </div>
              <div>
                <label className="text-xs text-brand-light/60">Restaurant name</label>
                <Input
                  value={f.restaurant_name}
                  onChange={setField("restaurant_name")}
                  className="bg-white text-brand-dark"
                  data-testid="signup-restname"
                />
                {errors.restaurant_name && <div className="text-xs text-red-400 mt-1">{errors.restaurant_name}</div>}
              </div>
              <div>
                <label className="text-xs text-brand-light/60">Phone</label>
                <Input
                  value={f.phone}
                  onChange={setField("phone")}
                  className="bg-white text-brand-dark"
                  data-testid="signup-phone"
                />
                {errors.phone && <div className="text-xs text-red-400 mt-1">{errors.phone}</div>}
              </div>
            </>
          )}

          <div>
            <label className="text-xs text-brand-light/60">Email</label>
            <Input
              value={f.email}
              onChange={setField("email")}
              className="bg-white text-brand-dark"
              autoComplete="email"
              data-testid="r-email"
            />
            {errors.email && <div className="text-xs text-red-400 mt-1">{errors.email}</div>}
          </div>
          <div>
            <label className="text-xs text-brand-light/60">Password</label>
            <Input
              type="password"
              value={f.password}
              onChange={setField("password")}
              className="bg-white text-brand-dark"
              autoComplete={mode === "login" ? "current-password" : "new-password"}
              data-testid="r-password"
            />
            {errors.password && <div className="text-xs text-red-400 mt-1">{errors.password}</div>}
          </div>
          {mode === "register" && (
            <div>
              <label className="text-xs text-brand-light/60">Confirm password</label>
              <Input
                type="password"
                value={f.confirmPassword}
                onChange={setField("confirmPassword")}
                className="bg-white text-brand-dark"
                data-testid="signup-confirm"
              />
              {errors.confirmPassword && <div className="text-xs text-red-400 mt-1">{errors.confirmPassword}</div>}
            </div>
          )}
          <Button
            onClick={submit}
            disabled={busy}
            className="w-full bg-brand-orange hover:bg-brand-orange-dark text-white"
            data-testid="r-submit"
          >
            {busy ? "Please wait..." : mode === "login" ? "Sign in" : "Create account"}
          </Button>
          <button
            onClick={() => { setMode(mode === "login" ? "register" : "login"); setErrors({}); }}
            className="text-xs text-brand-light/60 hover:text-brand-light w-full text-center"
            data-testid="switch-mode"
          >
            {mode === "login" ? "New here? Create an account →" : "Already have an account? Sign in →"}
          </button>
        </div>

        <div className="mt-6 text-center text-sm text-brand-light/60">
          Want to see it first?{" "}
          <Link to="/business/demo" className="text-brand-orange hover:underline" data-testid="book-demo-link">
            Book a demo →
          </Link>
        </div>
      </div>
    </div>
  );
}
